import React, { useState, useEffect } from 'react';
import { useGuestbookInfinite, useCreateGuestbookMutation } from '../api';
import { getUserName, saveUserNameFromModal, markNameModalSkipped } from '../utils/userName';
import { Button, PreviewNotice } from './ui';
import { PiPencilSimpleLine } from "react-icons/pi";
import NameModal from './NameModal';
import { useGallery } from '../context/GalleryContext';
import { PREVIEW_ONLY_MESSAGE, isPreviewReadOnlyError } from '../utils/previewReadOnly';

const Guestbook = () => {
  const { isPreview } = useGallery();
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [newMessageId, setNewMessageId] = useState(null);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [pendingName, setPendingName] = useState('');

  // Load name from localStorage on mount
  useEffect(() => {
    const savedName = getUserName();
    if (savedName) {
      setName(savedName);
      return;
    }
  }, []);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useGuestbookInfinite(50);

  const createGuestbookMutation = useCreateGuestbookMutation();

  // Flatten all pages of guestbook entries
  const allMessages = data?.pages?.flatMap(page => page.items) || [];

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Portal preview is read-only: the API rejects guest writes from a preview token.
    if (isPreview) {
      return;
    }

    if (!name.trim() || !message.trim()) {
      setSubmitMessage('Please fill in both name and message fields.');
      setTimeout(() => setSubmitMessage(''), 3000);
      return;
    }

    if (name.length > 200 || message.length > 2000) {
      setSubmitMessage('Name or message is too long. Please shorten your message.');
      setTimeout(() => setSubmitMessage(''), 3000);
      return;
    }

    setSubmitMessage('');

    try {
      const trimmedName = name.trim();
      const result = await createGuestbookMutation.mutateAsync({ name: trimmedName, message: message.trim() });
      // Save the trimmed name to localStorage
      saveUserNameFromModal(trimmedName);
      setName(trimmedName);
      setMessage('');
      setTimeout(() => setSubmitMessage(''), 3000);
      
      // Track the new message ID for animation
      if (result && result.id) {
        setNewMessageId(result.id);
        // Remove animation class after animation completes
        setTimeout(() => setNewMessageId(null), 500);
      }
    } catch (err) {
      console.error('Message submission error:', err);
      const errorMsg = isPreviewReadOnlyError(err)
        ? PREVIEW_ONLY_MESSAGE
        : err.response?.data || err.message || 'Failed to submit message. Please try again.';
      setSubmitMessage(`${errorMsg}`);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to extract user-friendly error message
  const getErrorMessage = (error) => {
    if (!error) return 'An unknown error occurred';

    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.response.statusText || '';

      if (status === 401) {
        return 'Unauthorized. Please log in again.';
      } else if (status === 403 || status === 503) {
        return 'Access denied. Please verify your permissions.';
      } else if (status === 404) {
        return 'Guestbook not found.';
      } else if (status === 429) {
        return 'Too many requests. Please try again later.';
      } else if (status === 500) {
        return 'Server error. Please try again later.';
      } else if (typeof message === 'string' && message) {
        return message;
      }
      
      return `Failed to load guestbook (Error ${status})`;
    }

    if (error.request) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    if (typeof error.message === 'string' && error.message) {
      return error.message;
    }

    return 'An error occurred while loading guestbook. Please try again.';
  };

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="text-center mb-8">
          <div className="h-8 bg-primary-20 rounded-lg w-64 mx-auto mb-2 animate-pulse"></div>
          <div className="h-4 bg-primary-10 rounded w-48 mx-auto animate-pulse"></div>
        </div>
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-6 mb-6">
          <div className="space-y-4">
            <div className="h-4 bg-primary-10 rounded w-32 animate-pulse"></div>
            <div className="h-12 bg-primary-10 rounded animate-pulse"></div>
            <div className="h-24 bg-primary-10 rounded animate-pulse"></div>
            <div className="h-10 bg-primary-20 rounded w-32 animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (error) {
    const errorMessage = getErrorMessage(error);
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="text-center max-w-md">
          <svg className="mx-auto h-16 w-16 text-red-500 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
          </svg>
          <h2 className="text-xl font-semibold text-secondary mb-3">Error Loading Guestbook</h2>
          <p className="text-secondary-70 mb-6 leading-relaxed">{errorMessage}</p>
          <Button
            onClick={() => refetch()}
            variant="primary"
            size="lg"
            icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-b from-accent-20 to-accent-10 relative">
      <NameModal
        isOpen={isNameModalOpen}
        pendingName={pendingName}
        onChangePendingName={setPendingName}
        onCancel={() => {
          markNameModalSkipped();
          setIsNameModalOpen(false);
        }}
        onSave={() => {
          const trimmed = saveUserNameFromModal(pendingName);
          if (!trimmed) {
            return;
          }
          setName(trimmed);
          setPendingName('');
          setIsNameModalOpen(false);
        }}
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-2">
            <h1 className="text-2xl font-semibold text-secondary">
              Guestbook
            </h1>
          </div>
          <p className="text-secondary-70">
            Share your thoughts and well wishes with the couple.
          </p>
        </div>

        {/* Message Form */}
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <span className="block text-sm font-medium text-secondary mb-1">
                Your Name
              </span>
              <div className="flex items-center gap-2 text-xs text-secondary-80">
                <div className="flex-1 min-w-0 truncate">
                  {name ? (
                    <span className="truncate block">{name}</span>
                  ) : (
                    <span className="italic text-secondary-60 truncate block">
                      You have not set your name
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="inline-flex flex-shrink-0 items-center gap-1 text-xs text-primary hover:text-primary-80 transition-colors"
                  onClick={() => {
                    setPendingName(name || '');
                    setIsNameModalOpen(true);
                  }}
                >
                  <PiPencilSimpleLine className="w-3 h-3" />
                  <span>Edit</span>
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="message-text" className="block text-sm font-medium text-secondary mb-2">
                Your Message
              </label>
              <textarea
                id="message-text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your message here..."
                maxLength={2000}
                rows={4}
                className="w-full px-4 py-3 bg-accent-20 text-base sm:text-sm leading-relaxed border border-accent rounded-lg focus:ring-2 focus:ring-primary-50 focus:border-primary transition-colors placeholder-secondary-60 resize-none"
                required
              />
              <div className="text-xs text-secondary-60 mt-1 text-right">
                {message.length}/2000
              </div>
            </div>

            {submitMessage && (
              <div className="p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200">
                {submitMessage}
              </div>
            )}

            {isPreview ? <PreviewNotice id="guestbook-preview-notice" /> : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={isPreview || createGuestbookMutation.isPending || !name.trim() || !message.trim()}
              isLoading={createGuestbookMutation.isPending}
              aria-describedby={isPreview ? 'guestbook-preview-notice' : undefined}
              icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>}
            >
              Submit Message
            </Button>
          </form>
        </div>

        {/* Guestbook List */}
        <div className="bg-surface rounded-xl shadow-sm border border-accent-50 p-4 sm:p-6">
          <h2 className="text-lg font-medium text-secondary mb-4">
            Guestbook ({allMessages.length})
          </h2>

          {allMessages.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-16 w-16 text-primary-40 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-secondary-70">No entries yet. Be the first to share your thoughts!</p>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-6">
                {allMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`bg-accent-10 rounded-lg p-4 border border-accent-30 hover:shadow-md transition-shadow ${
                      newMessageId === msg.id ? 'successful-post-animation' : ''
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                      <h3 className="font-medium text-secondary">
                        {msg.name}
                      </h3>
                      {msg.createdAt && (
                        <span className="text-xs text-secondary-60 whitespace-nowrap">
                          {formatDate(msg.createdAt)}
                        </span>
                      )}
                    </div>
                    <p className="text-secondary-80 text-sm leading-relaxed whitespace-pre-wrap">
                      {msg.message}
                    </p>
                  </div>
                ))}
              </div>

              {hasNextPage && (
                <div className="text-center">
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    variant="primary"
                    size="md"
                    isLoading={isFetchingNextPage}
                    icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>}
                  >
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Guestbook;
